import {
  CONFIRMED_EMPTY_GATE_BY_KEY,
  MY_SAME_QUESTION_ID,
  PAST_SEASON_DELTA_IDS,
  SAME_AS_CURRENT,
  TAX_SETUP_QUESTIONS,
} from "v2/constants/taxSetupQuestions";
import { isQuestionAnswered } from "v2/utils/isQuestionAnswered";
import { fromScopePayload } from "v2/utils/taxSetupPayload";

// The gates own no payload key of their own — their answer lives in the array
// their follow-up writes — but they are still questions the customer answers.
const CONFIRMED_EMPTY_GATE_IDS = new Set(
  Object.values(CONFIRMED_EMPTY_GATE_BY_KEY)
);

const CURRENT_TOP_LEVEL = TAX_SETUP_QUESTIONS.filter(
  (question) => question.payloadKey || CONFIRMED_EMPTY_GATE_IDS.has(question.id)
);
const CURRENT_QUESTION_IDS = CURRENT_TOP_LEVEL.map((question) => question.id);

const percent = (answered: number, total: number): number =>
  total === 0 ? 0 : Math.round((answered / total) * 100);

const EMPTY_ANSWERS: Record<string, unknown> = {};

type SeasonCount = { answered: number; total: number };

const countSeasonAnswers = (
  answers: Record<string, unknown>,
  season: number,
  currentSeason: number
): SeasonCount => {
  const scoped = fromScopePayload(answers);

  if (season === currentSeason) {
    return {
      answered: CURRENT_QUESTION_IDS.filter((id) =>
        isQuestionAnswered(scoped[id])
      ).length,
      total: CURRENT_QUESTION_IDS.length,
    };
  }

  const mySame = scoped[MY_SAME_QUESTION_ID];

  if (!isQuestionAnswered(mySame)) return { answered: 0, total: 1 };
  if (mySame === SAME_AS_CURRENT) return { answered: 1, total: 1 };

  const answeredDeltas = PAST_SEASON_DELTA_IDS.filter((id) =>
    isQuestionAnswered(scoped[id])
  ).length;

  return {
    answered: 1 + answeredDeltas,
    total: 1 + PAST_SEASON_DELTA_IDS.length,
  };
};

export const countEntityQuestions = (
  dataBySeason: Record<string, Record<string, unknown>> | undefined,
  seasons: number[],
  currentSeason: number
): SeasonCount => {
  let answered = 0;
  let total = 0;

  seasons.forEach((season) => {
    const count = countSeasonAnswers(
      dataBySeason?.[season] ?? EMPTY_ANSWERS,
      season,
      currentSeason
    );
    answered += count.answered;
    total += count.total;
  });

  return { answered, total };
};

export const computeEntityProgress = (
  dataBySeason: Record<string, Record<string, unknown>> | undefined,
  seasons: number[],
  currentSeason: number
): number => {
  const { answered, total } = countEntityQuestions(
    dataBySeason,
    seasons,
    currentSeason
  );

  return percent(answered, total);
};

export const isSeasonComplete = (
  answers: Record<string, unknown> | undefined,
  season: number,
  currentSeason: number
): boolean => {
  const { answered, total } = countSeasonAnswers(
    answers ?? EMPTY_ANSWERS,
    season,
    currentSeason
  );

  return total > 0 && answered === total;
};

export const isEntityComplete = (progress: number): boolean => progress === 100;
